import React, {useEffect, useState} from "react";
import { Modal, Button, Label, Row, Col, Nav, NavItem, Tab, Tabs } from "react-bootstrap";
import { View } from "react-native";
import { getCapHealthLog, getCapNameChanges } from "../utils/bedrockAPI";
import "./CapLogModal.css";

export default function CapLogModal({show, setShow, capId, capName}) {
    const [activeTab, setActiveTab] = useState('health');
    const [healthLog, setHealthLog] = useState([]);
    const [nameChanges, setNameChanges] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (show && capId) {
            loadHealthLog();
            loadNameChanges();
        }
    }, [show, capId]);

    async function loadHealthLog() {
        setIsLoading(true);
        try {
            const log = await getCapHealthLog(capId);
            setHealthLog(log || []);
        } catch (error) {
            console.error('Error loading health log:', error);
            setHealthLog([]);
        }
        setIsLoading(false);
    }

    async function loadNameChanges() {
        try {
            const changes = await getCapNameChanges(capId);
            setNameChanges(changes || []);
        } catch (error) {
            console.error('Error loading name changes:', error);
            setNameChanges([]);
        }
    }

    function getHealthStyle(health) {
        switch(health) {
            case 'ONLINE':
                return 'success';
            case 'BATTERY':
                return 'warning';
            case 'POWER_LOSS':
            case 'ERROR':
                return 'danger';
            case 'DOWN':
                return 'default';
            default:
                return 'default';
        }
    }

    function formatDate(dateString) {
        const date = new Date(dateString);
        return date.toLocaleString();
    }

    function formatDuration(startDate, endDate) {
        if (!endDate) return 'Ongoing';
        
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffMs = end - start;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        
        if (diffHours > 0) {
            return `${diffHours}h ${diffMinutes}m`;
        } else {
            return `${diffMinutes}m`;
        }
    }

    function renderHealthLog() {
        if (isLoading) {
            return (
                <div className="log-loading-message">
                    Loading health log...
                </div>
            );
        }
        
        if (healthLog.length === 0) {
            return (
                <div className="log-empty-message">
                    No health log data available
                </div>
            );
        }

        return (
            <div>
                <div className="log-header">
                    <Row>
                        <Col md={3}>Health Status</Col>
                        <Col md={3}>Start Time</Col>
                        <Col md={3}>End Time</Col>
                        <Col md={3}>Duration</Col>
                    </Row>
                </div>
                {healthLog.map((entry, index) => (
                    <div 
                        key={entry.capHealthId || index} 
                        className="log-row"
                    >
                        <Row>
                            <Col md={3}>
                                <Label bsStyle={getHealthStyle(entry.health)}>
                                    {entry.health}
                                </Label>
                            </Col>
                            <Col md={3}>
                                <span className="log-date">
                                    {formatDate(entry.addDate)}
                                </span>
                            </Col>
                            <Col md={3}>
                                <span className="log-date">
                                    {entry.updateDate ? formatDate(entry.updateDate) : '-'}
                                </span>
                            </Col>
                            <Col md={3}>
                                <span className="log-duration">
                                    {formatDuration(
                                        entry.addDate, 
                                        entry.updateDate || (index > 0 ? healthLog[index - 1].addDate : null)
                                    )}
                                </span>
                            </Col>
                        </Row>
                    </div>
                ))}
            </div>
        );
    }

    function renderNameChanges() {
        if (nameChanges.length === 0) {
            return (
                <div className="log-empty-message">
                    No name changes recorded
                </div>
            );
        }

        return (
            <div>
                <div className="log-header">
                    <Row>
                        <Col md={3}>Old Name</Col>
                        <Col md={3}>New Name</Col>
                        <Col md={3}>Changed By</Col>
                        <Col md={3}>Date</Col>
                    </Row>
                </div>
                {nameChanges.map((change, index) => (
                    <div 
                        key={index} 
                        className="log-row"
                    >
                        <Row>
                            <Col md={3}>
                                <span className="name-change-old">
                                    {change.oldName || '-'}
                                </span>
                            </Col>
                            <Col md={3}>
                                <span className="name-change-new">
                                    {change.newName || '-'}
                                </span>
                            </Col>
                            <Col md={3}>
                                <span className="name-change-user">
                                    {change.changedByUser ? 
                                        `${change.changedByUser.firstName} ${change.changedByUser.lastName}` :
                                        change.changedBy || '-'
                                    }
                                </span>
                            </Col>
                            <Col md={3}>
                                <span className="log-date">
                                    {change.changedAt ? formatDate(change.changedAt) : '-'}
                                </span>
                            </Col>
                        </Row>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <Modal
            show={show}
            onHide={() => setShow(false)}
            size="lg"
            className="cap-log-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    Cap Logs - {capName}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Tabs 
                    activeKey={activeTab} 
                    onSelect={setActiveTab}
                    id="cap-log-tabs"
                    className="cap-log-tabs"
                >
                    <Tab eventKey="health" title="Health Log">
                        {renderHealthLog()}
                    </Tab>
                    <Tab eventKey="nameChanges" title="Name Changes">
                        {renderNameChanges()}
                    </Tab>
                </Tabs>
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={() => setShow(false)}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
